import { useEffect, useRef, useState } from 'react';
import { CreditCard, Loader, X, CheckCircle, AlertCircle } from 'lucide-react';

// Loads the Square Web Payments SDK script once and resolves when ready
let squareSdkPromise = null;
function loadSquareSdk(environment) {
  if (squareSdkPromise) return squareSdkPromise;

  squareSdkPromise = new Promise((resolve, reject) => {
    if (window.Square) {
      resolve(window.Square);
      return;
    }
    const script = document.createElement('script');
    script.src = environment === 'production'
      ? 'https://web.squarecdn.com/v1/square.js'
      : 'https://sandbox.web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => {
      if (window.Square) resolve(window.Square);
      else reject(new Error('Square SDK loaded but window.Square is missing'));
    };
    script.onerror = () => reject(new Error('Failed to load Square Web Payments SDK'));
    document.head.appendChild(script);
  });

  return squareSdkPromise;
}

export default function SquarePaymentModal({
  invoice,
  companyId,
  squareLocationId,
  companyName,
  onClose,
  onPaymentSuccess,
}) {
  const cardContainerRef = useRef(null);
  const cardInstanceRef = useRef(null);
  const paymentsInstanceRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const configResp = await fetch('/api/square-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'get-config' }),
        });
        const config = await configResp.json();
        if (!config.applicationId) throw new Error('Square is not configured');

        const Square = await loadSquareSdk(config.environment);
        if (cancelled) return;

        const payments = Square.payments(config.applicationId, squareLocationId);
        paymentsInstanceRef.current = payments;

        const card = await payments.card();
        await card.attach(cardContainerRef.current);
        cardInstanceRef.current = card;

        setSdkReady(true);
      } catch (err) {
        console.error('Square SDK init failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load payment form');
      }
    }

    init();

    return () => {
      cancelled = true;
      if (cardInstanceRef.current) {
        try { cardInstanceRef.current.destroy(); } catch (e) { /* noop */ }
        cardInstanceRef.current = null;
      }
    };
  }, [squareLocationId]);

  const handlePay = async () => {
    if (!cardInstanceRef.current) return;
    setSubmitting(true);
    setError(null);

    try {
      const tokenResult = await cardInstanceRef.current.tokenize();
      if (tokenResult.status !== 'OK') {
        const msg = tokenResult.errors?.[0]?.message || 'Card could not be processed';
        setError(msg);
        setSubmitting(false);
        return;
      }

      const amountCents = Math.round(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0)) * 100);

      const resp = await fetch('/api/square-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create-payment',
          companyId,
          amount: amountCents,
          currency: 'USD',
          source_id: tokenResult.token,
          invoiceId: invoice.id,
          customerEmail: invoice.customer_email,
          note: `Invoice #${invoice.invoice_number || ''}`.trim(),
        }),
      });

      const data = await resp.json();
      if (!resp.ok || !data.success) {
        const detail = data.details?.[0]?.detail || data.error || 'Payment failed';
        setError(detail);
        setSubmitting(false);
        return;
      }

      setSuccess(data.payment);
      if (onPaymentSuccess) onPaymentSuccess(data.payment);
    } catch (err) {
      console.error('Square payment error:', err);
      setError(err.message || 'Payment failed');
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        maxWidth: '440px',
        width: '100%',
        border: '1px solid #3a3a3a',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid #3a3a3a',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#fff', fontWeight: 600 }}>
            <CreditCard size={18} />
            Pay with Square
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <CheckCircle size={48} color="#4ecca3" style={{ marginBottom: '0.75rem' }} />
              <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Payment Successful
              </div>
              <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>
                ${(success.amount?.amount / 100).toFixed(2)} charged to card
              </div>
              {success.receipt_url && (
                <a
                  href={success.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#4ecca3', fontSize: '0.85rem', textDecoration: 'underline' }}
                >
                  View receipt
                </a>
              )}
              <button
                onClick={onClose}
                style={{
                  display: 'block',
                  margin: '1.25rem auto 0',
                  padding: '0.6rem 1.25rem',
                  background: '#006AFF',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: '1rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid #2a2a2a',
              }}>
                <div>
                  <div style={{ color: '#888', fontSize: '0.8rem' }}>{companyName || 'Invoice'}</div>
                  <div style={{ color: '#fff', fontSize: '0.95rem' }}>
                    Invoice #{invoice.invoice_number}
                  </div>
                </div>
                <div style={{ color: '#4ecca3', fontSize: '1.4rem', fontWeight: 700 }}>
                  ${(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0))).toFixed(2)}
                </div>
              </div>

              <div
                ref={cardContainerRef}
                style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  minHeight: '90px',
                  marginBottom: '0.75rem',
                }}
              />

              {!sdkReady && !error && (
                <div style={{ color: '#888', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
                  <Loader size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '0.5rem', display: 'inline' }} />
                  Loading payment form...
                </div>
              )}

              {error && (
                <div style={{
                  marginBottom: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: '#e74c3c20',
                  borderRadius: '6px',
                  color: '#e74c3c',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}>
                  <AlertCircle size={14} /> {error}
                </div>
              )}

              <button
                onClick={handlePay}
                disabled={!sdkReady || submitting}
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  background: !sdkReady || submitting ? '#3a3a3a' : '#006AFF',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: !sdkReady || submitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
              >
                {submitting ? (
                  <>
                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Processing...
                  </>
                ) : (
                  <>Pay ${(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0))).toFixed(2)}</>
                )}
              </button>

              <div style={{
                textAlign: 'center',
                color: '#666',
                fontSize: '0.75rem',
                marginTop: '0.75rem',
              }}>
                Secured by Square
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
